import ClickableTableCell from 'Components/ClickableTableCell'
import CustomTablePagination from 'Components/CustomTablePagination'
import EnhancedTableHead from 'Components/EnhancedTableHead'
import type { HeadCell } from 'Components/EnhancedTableHead/EnhancedTableHead'
import StyledTableRow from 'Components/StyledTableRow'
import { WithLoader } from 'Components/WithLoader'
import { useAuthorityContext } from 'Services/common/Features/Authority/Context/authorityContext'
import type {
  FetchTicketSaleTargetListParams,
  TicketSaleTarget
} from 'Services/SalesManagement/Features/TicketSale/types'
import { datetimeFormatter } from 'Util/formatters'
import { Paper, Table, TableBody, TableCell, TableContainer } from '@mui/material'
import { t } from 'ttag'

interface TicketSaleTargetListProps {
  rows: TicketSaleTarget[]
  isLoading: boolean
  params: FetchTicketSaleTargetListParams
  handleRequestSort: (event: React.MouseEvent<unknown, MouseEvent>, property: string) => void
  handleChangePage: (event: unknown, newPage: number) => void
  count: number
  page: number
  onRowClick: (id: string) => void
  selectedTicketSaleTargetId?: string
}

export default function TicketSaleTargetList({
  rows,
  isLoading,
  params,
  handleRequestSort,
  handleChangePage,
  count,
  page,
  onRowClick,
  selectedTicketSaleTargetId
}: Readonly<TicketSaleTargetListProps>) {
  const headCells: HeadCell[] = [
    { id: 'identifier', label: t`Identifier` },
    { id: 'authority', label: t`Authority` },
    { id: 'created', label: t`Created`, sortable: true }
  ]
  const { getAuthorityNameByCode } = useAuthorityContext()
  return (
    <WithLoader loading={isLoading}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table size="small" aria-label={t`Ticket sale targets table`}>
            <EnhancedTableHead
              orderBy={params.ordering}
              headCells={headCells}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {rows.map(row => (
                <StyledTableRow
                  hover
                  tabIndex={-1}
                  key={row.id}
                  selected={selectedTicketSaleTargetId === row.id}
                >
                  <ClickableTableCell onClick={() => onRowClick(row.id)}>
                    {row.identifier}
                  </ClickableTableCell>
                  <TableCell>{getAuthorityNameByCode(row.authority)}</TableCell>
                  <TableCell>{datetimeFormatter(row.created)}</TableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <CustomTablePagination count={count} page={page} onPageChange={handleChangePage} />
      </Paper>
    </WithLoader>
  )
}
